import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  Banner,
  Icon,
  PageFrame,
  PageHeader,
  Progress,
  WS_COLOR,
  WorkspaceThemeProvider,
  icons,
} from '@platform/ui-kit/workspace';
import { api } from '../../../api';

/**
 * Strategic Intelligence → Platform Analysis, as a Decision Workspace screen.
 *
 * Shown with `?ui=workspace` next to the ported original in `PlatformAnalysis.tsx`, which stays
 * the default until the owner approves this one (CLAUDE.md 1.2).
 *
 * WHAT IS UNCHANGED: the four API calls, the job-and-poll loop (first poll after 1.5 s, every
 * 3 s, give up at 180 s), every message, both field labels and hints, the 2000-character limit
 * on the notes, the selection-criteria count and its warning, the required name, and the
 * two-states-on-one-route behaviour (the form, then the progress view, which opens the
 * platform when it is ready).
 *
 * WHAT CHANGES, by the design system and nothing else: the page frame (locked header, the body
 * is the one scroll region, the buttons are a fixed row at the foot), messages as banners, the
 * waiting state as a labelled progress bar, and the source's Materialize-styled Analyze and
 * Cancel buttons as the kit's filled and tonal buttons.
 */

const CATALOG = '/ai_readiness/strategic-intelligence/ai-platforms-catalog';

export function PlatformAnalysisWorkspace() {
  return (
    <WorkspaceThemeProvider baseline={false}>
      <Screen />
    </WorkspaceThemeProvider>
  );
}

function Screen() {
  const navigate = useNavigate();
  // Stay in the workspace version: the switch lives in the query string.
  const { search } = useLocation();
  const [name, setName] = useState('');
  const [context, setContext] = useState('');
  const [criteriaCount, setCriteriaCount] = useState<number | null>(null);

  const [jobId, setJobId] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);

  /** Cleared on unmount so a poll cannot fire into a screen that has gone. */
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  useEffect(() => {
    api.aiReadiness
      .getPlatformAnalysisCriteria()
      .then((d) => setCriteriaCount(d.criteriaCount))
      .catch(() => setCriteriaCount(null));
  }, []);

  /* The original's loop exactly: first poll after 1.5s, every 3s after that, give up at 180. */
  const poll = useCallback(
    (id: string, seconds: number) => {
      api.aiReadiness
        .getPlatformAnalysisStatus(id)
        .then((d) => {
          if (d.status === 'done' && d.platformId) {
            navigate(`${CATALOG}/${d.platformId}${search}`);
            return;
          }
          if (d.status === 'error') {
            setRunning(false);
            setError(`Analysis failed: ${d.error || 'unknown error'}.`);
            return;
          }
          const next = seconds + 3;
          if (next > 180) {
            setRunning(false);
            setError(
              'This is taking longer than expected. The analysis may still be running — check the catalog shortly.',
            );
            return;
          }
          setElapsed(next);
          timer.current = setTimeout(() => poll(id, next), 3000);
        })
        .catch(() => {
          const next = seconds + 3;
          if (next > 180) {
            setRunning(false);
            setError('Lost connection while analyzing.');
            return;
          }
          setElapsed(next);
          timer.current = setTimeout(() => poll(id, next), 3000);
        });
    },
    [navigate, search],
  );

  const analyze = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Platform name is required.');
      return;
    }
    setError(null);
    setRunning(true);
    setElapsed(0);
    api.aiReadiness
      .startPlatformAnalysis(trimmed, context)
      .then((d) => {
        setJobId(d.jobId);
        timer.current = setTimeout(() => poll(d.jobId, 0), 1500);
      })
      .catch((e: Error) => {
        setRunning(false);
        setError(e.message);
      });
  };

  const toCatalog = () => navigate(`${CATALOG}${search}`);

  if (running || jobId) {
    return (
      <PageFrame
        header={
          <PageHeader
            title={`Analyzing ${name}`}
            subtitle="Building the full profile — capability read, practical heatmap, and Function & Industry coverage. This usually takes under a minute. You can keep this tab open; it will open the platform automatically when ready."
          />
        }
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', maxWidth: 720 }} tabIndex={0}>
          {running ? (
            <Box role="status">
              <Progress label={`Working…${elapsed ? ` (${elapsed}s)` : ''}`} />
            </Box>
          ) : null}
          {error ? (
            <Box sx={{ mt: running ? 4 : 0, display: 'grid', gap: 3, justifyItems: 'start' }}>
              <Banner tone="error">{error}</Banner>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setJobId(null);
                    setError(null);
                    setElapsed(0);
                  }}
                >
                  Try again
                </Button>
                <Button variant="tonal" onClick={toCatalog}>
                  Back to catalog
                </Button>
              </Box>
            </Box>
          ) : null}
        </Box>
      </PageFrame>
    );
  }

  return (
    <PageFrame
      header={
        <PageHeader
          title="Analyze an AI Platform"
          subtitle="Enter an AI platform and we'll analyze it and add it to your AI Platforms catalog with a practical profile (how it runs, costs, deploys, and connects) you can review and refine."
        />
      }
      footer={
        <Box sx={{ display: 'flex', gap: 2, pt: 3 }}>
          <Button
            variant="contained"
            onClick={analyze}
            disabled={!name.trim()}
            startIcon={<Icon icon={icons.Sparkles} size={16} />}
          >
            Analyze
          </Button>
          <Button variant="tonal" onClick={toCatalog}>
            Cancel
          </Button>
        </Box>
      }
    >
      <Box
        tabIndex={0}
        sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', maxWidth: 820 }}
      >
        {/* The selection criteria are the only thing a platform is scored against (owner, 2026-08-17). */}
        {criteriaCount === 0 ? (
          <Box sx={{ mb: 4 }}>
            <Banner tone="warning">
              No AI Platform Selection Criteria are selected, so there is nothing to score a
              platform against. Choose at least one on Settings → AI Platform Selection → Platform
              Fitment Criteria.
            </Banner>
          </Box>
        ) : criteriaCount ? (
          <Typography variant="caption" sx={{ display: 'block', mb: 4, color: WS_COLOR.textMuted }}>
            The platform will be scored against {criteriaCount} selection criteri
            {criteriaCount === 1 ? 'on' : 'a'}.
          </Typography>
        ) : null}

        {error ? (
          <Box sx={{ mb: 4 }}>
            <Banner tone="error">{error}</Banner>
          </Box>
        ) : null}

        <Typography variant="h2" component="h2" sx={{ mb: 3 }}>
          Platform Details
        </Typography>
        <Box sx={{ display: 'grid', gap: 4 }}>
          <TextField
            fullWidth
            required
            label="AI Platform Name"
            helperText="e.g. Anthropic Claude, Dataiku, Cohere"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Context / notes for the analysis (optional)"
            helperText="Anything specific about the client's environment, the opportunity, or what to weigh."
            value={context}
            onChange={(e) => setContext(e.target.value)}
            inputProps={{ maxLength: 2000 }}
          />
        </Box>
      </Box>
    </PageFrame>
  );
}
